import type { App } from "./App";
import type { StateConfigurationGS } from "../state/StateConfigurationGS";
import type { StateRuntimeGS } from "../state/StateRuntimeGS";

export enum RuntimeEnvironmentGSEnum {
    Browser,
    Server
}

/** Mutable data owned by one browser application or one server request. */
export interface RuntimeScopeGS {
    readonly app: App;
    readonly environment: RuntimeEnvironmentGSEnum;
    readonly instances: Map<Function, unknown>;
    document?: Document;
    window?: Window;
    request?: Request;
    url?: string;
    language?: string;
    pendingPageElement?: HTMLElement;
    stateConfigurationOpen?: boolean;
    stateRuntimeFactory?: (app: App, configuration: StateConfigurationGS) => StateRuntimeGS;
}

interface RuntimeScopeRunnerGS {
    getStore(): RuntimeScopeGS | undefined;
    run<T>(scope: RuntimeScopeGS, action: () => Promise<T>): Promise<T>;
}

/**
 * Internal scope boundary used by the static GS facade.
 * Browser code owns one persistent scope. The server adapter installs an
 * AsyncLocalStorage-backed runner so parallel requests never share mutable data.
 */
export class RuntimeGS {
    private static _browserScope?: RuntimeScopeGS;
    private static _fallbackScope?: RuntimeScopeGS;
    private static _scopeRunner?: RuntimeScopeRunnerGS;

    public static get current(): RuntimeScopeGS | undefined {
        return RuntimeGS._scopeRunner?.getStore()
            ?? RuntimeGS._fallbackScope
            ?? RuntimeGS._browserScope;
    }

    public static get document(): Document {
        const scoped = RuntimeGS.current?.document;
        if (scoped) return scoped;
        if (typeof document !== "undefined") return document;
        throw new Error("GS DOM is unavailable outside a browser or render scope.");
    }

    public static get window(): Window | undefined {
        const scoped = RuntimeGS.current?.window;
        if (scoped) return scoped;
        return typeof window === "undefined" ? undefined : window;
    }

    public static setBrowserScope(scope: RuntimeScopeGS): void {
        RuntimeGS._browserScope = scope;
    }

    public static installScopeRunner(runner: RuntimeScopeRunnerGS): void {
        RuntimeGS._scopeRunner = runner;
    }

    public static async run<T>(scope: RuntimeScopeGS, action: () => Promise<T>): Promise<T> {
        if (RuntimeGS._scopeRunner) return RuntimeGS._scopeRunner.run(scope, action);

        const previous = RuntimeGS._fallbackScope;
        RuntimeGS._fallbackScope = scope;
        try {
            return await action();
        } finally {
            RuntimeGS._fallbackScope = previous;
        }
    }
}
